import { h, Fragment } from 'preact';
import { useContext } from 'preact/hooks';
import classNames from 'classnames';
import FileDocumentOutline from 'widget_main/globals/assets/fileDocumentOutline.svg';
import ChevronRight from 'widget_main/globals/assets/chevronRight.svg';
import * as keyboardEventHelpers from 'widget_main/globals/keyboardEvents';
import { KB_DEFLECT_ARTICLE_PATH } from 'widget_main/globals/constants';
import {
  NavigationContext,
  KBContext,
  MessagesContext,
  SettingsContext,
} from 'widget_main/store';
import { ArticleCallbackResponse } from 'core_main/sdk/get_articles/types';
import {
  getKustomerCore,
  getMessageAgentInformation,
  truncate,
} from 'widget_main/globals/helpers';
import { getMappedLanguage } from 'i18n/helpers';
import Markdown from 'widget_main/components/Markdown/Markdown';
import { Message } from '../types';
import ChatBubble from '../ChatBubble';

import styles from './templateMessage.scss';

const kustomerCore = getKustomerCore();

interface TemplateMessageProps {
  showAvatar?: boolean;
  showTimestamp?: boolean;
  message?: Message;
  chatBubbleInnerClassName?: string;
  truncateLength?: number;
}

const TemplateMessage = ({
  message,
  showAvatar,
  showTimestamp,
  chatBubbleInnerClassName,
  truncateLength,
}: TemplateMessageProps) => {
  const navigation = useContext(NavigationContext);
  const messages = useContext(MessagesContext);
  const settings = useContext(SettingsContext);
  const kb = useContext(KBContext);

  const lang = getMappedLanguage();

  const navigateToArticleView = (articleId) => {
    messages.markArticleVisited(message?.messageId, articleId);
    kb.updateCurrentArticleId(articleId);
    navigation.updatePage(KB_DEFLECT_ARTICLE_PATH);
  };

  const handleClickArticle = (article: ArticleCallbackResponse) => {
    const { articleId, knowledgeBaseId } = article;

    if (kb.articles?.[articleId]) {
      return navigateToArticleView(articleId);
    }

    return kustomerCore.getArticleById(
      {
        articleId,
        lang,
        knowledgeBaseId,
      },
      (response) => {
        kb.updateArticles([response]);
        navigateToArticleView(articleId);
      },
    );
  };

  const handleKeyPress = (e, article: ArticleCallbackResponse) => {
    if (keyboardEventHelpers.isEnterKey(e)) {
      e.preventDefault();
      e.stopPropagation();

      handleClickArticle(article);
    }
  };

  const renderMarkdown = (markdown?: string) => {
    if (!markdown) {
      return null;
    }

    const truncated = truncateLength
      ? truncate(markdown, truncateLength)
      : markdown;

    return <Markdown markdown={truncated} options={{ breaks: true }} />;
  };

  const renderStandardMessage = () => {
    const agentData = getMessageAgentInformation(
      message?.sentBy,
      settings,
      navigation.initAssistantPayload,
    );
    return (
      <ChatBubble
        className={classNames({
          [styles.isLastInStreak]: showAvatar,
        })}
        innerClassName={classNames(chatBubbleInnerClassName)}
        showAvatar={showAvatar}
        avatarUrl={agentData.avatarUrl}
        userDisplayName={agentData.displayName}
        showTimestamp={showTimestamp}
        timestamp={message?.createdAt}
      >
        {renderMarkdown(message?.meta?.template?.body)}
      </ChatBubble>
    );
  };

  const renderDeflectionMessage = () => {
    const agentData = getMessageAgentInformation(
      message?.sentBy,
      settings,
      navigation.initAssistantPayload,
    );
    return (
      <Fragment>
        <ChatBubble innerClassName={classNames(chatBubbleInnerClassName)}>
          {renderMarkdown(message?.body)}
        </ChatBubble>
        <ChatBubble
          innerClassName={classNames(
            styles.kbArticlesChatBubble,
            chatBubbleInnerClassName,
          )}
        >
          {message?.meta?.articles?.map((article) => {
            return (
              <div
                key={article.articleId}
                className={styles.kbArticle}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => handleKeyPress(e, article)}
                onClick={() => handleClickArticle(article)}
              >
                <div className={styles.articleIcon}>
                  <FileDocumentOutline width={14} height={14} />
                </div>
                <div className={styles.articleDetails}>
                  <div className={styles.articleTitle} data-kt="kbArticleTitle">
                    {article.title}
                  </div>
                </div>
                <ChevronRight className={styles.chevronRight} />
              </div>
            );
          })}
        </ChatBubble>
        <ChatBubble
          className={classNames({
            [styles.isLastInStreak]: showAvatar,
          })}
          innerClassName={classNames(chatBubbleInnerClassName)}
          showAvatar={showAvatar}
          avatarUrl={agentData.avatarUrl}
          showTimestamp={showTimestamp}
          userDisplayName={agentData.displayName}
          timestamp={message?.createdAt}
        >
          {renderMarkdown(message?.meta?.template?.followupText)}
        </ChatBubble>
      </Fragment>
    );
  };

  const templateType = message?.meta?.template?.templateType;

  switch (templateType) {
    case 'text':
    case 'quick_replies':
    case 'mll':
      return renderStandardMessage();
    case 'deflection':
      return renderDeflectionMessage();
    default:
      return renderStandardMessage();
  }
};
export default TemplateMessage;
